import { CalendarToday } from "@mui/icons-material";
import { Box, Grid, MenuItem, Select } from "@mui/material";
import { Typography, Divider, Button } from "@mui/material";
import { t } from "i18next";
import { useState, useEffect } from "react";
import { useSetRecoilState } from "recoil";
import { DescriptionIcon, ArrowForwardIosIcon, VisibilityIcon, BackupTableIcon, FolderOffIcon } from "../../icons";
import { selectedReportListState } from "../../recoil";
import { getTemplates } from "../../services/reportService";
import { REPORT_CONTENTS } from "./ReportContents";
import { ContainedButtonStyles } from "../../styles/GlobalStyles";
import { EmptyTemplate } from "../../utils/utilsComponent";
import templateResonse from "../../datas/templates.json";

interface periodItems {
  year: number;
  quarter?: number;
}

interface TemplateModalProps {
  selectedPeriod: periodItems | null;
  setSelectedPeriod: React.Dispatch<React.SetStateAction<periodItems | null>>;
  setOpenCreateModal: (open: boolean) => void;
  dateRange: {
    start_date: string;
    end_date: string;
  };
  setDateRange: (dateRange: { start_date: string; end_date: string }) => void;
}

export default function TemplateModal({
  selectedPeriod,
  setSelectedPeriod,
  setOpenCreateModal,
  dateRange,
  setDateRange,
}: TemplateModalProps) {
  const setSelectedReportList = useSetRecoilState(selectedReportListState);
  const [templateList, setTemplateList] = useState<templateItem[]>([]);
  const [selectedTemplate, setSelectedTemplate] = useState<templateItem | null>(null);
  const [selectedTemContents, setSelectedTemContents] = useState<ContentItem[]>([]);

  const fetchTemplates = async () => {
    try {
      // const response = await getTemplates();
      const response = templateResonse;
      const filteredTemplates = response.templates.filter((item: any) => item.created_by == null);
      setTemplateList(filteredTemplates);
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    fetchTemplates();
  }, []);

  useEffect(() => {
    if (selectedTemplate) {
      const contents = JSON.parse(selectedTemplate.contents);
      setSelectedTemContents(contents);
    }
  }, [selectedTemplate]);

  const handlePeriodChange = (period: { year: number; quarter?: number }) => {
    setSelectedPeriod(period);
  };

  const handleCancel = () => {
    setOpenCreateModal(false);
    setSelectedTemplate(null);
  };

  const handleApply = () => {
    const contentsWithIds = selectedTemContents.map((item: any) => {
      const uniqueRandomNumber = String(Math.floor(100000 + Math.random() * 900000));
      return {
        ...item,
        id: `${item.type}-${uniqueRandomNumber}`,
      };
    });

    if (selectedPeriod?.quarter) {
      if (selectedPeriod.quarter === 1) {
        setDateRange({
          start_date: `${selectedPeriod.year}-01-01`,
          end_date: `${selectedPeriod.year}-03-31`,
        });
      } else if (selectedPeriod.quarter === 2) {
        setDateRange({
          start_date: `${selectedPeriod.year}-04-01`,
          end_date: `${selectedPeriod.year}-06-30`,
        });
      } else if (selectedPeriod.quarter === 3) {
        setDateRange({
          start_date: `${selectedPeriod.year}-07-01`,
          end_date: `${selectedPeriod.year}-09-30`,
        });
      } else if (selectedPeriod.quarter === 4) {
        setDateRange({
          start_date: `${selectedPeriod.year}-10-01`,
          end_date: `${selectedPeriod.year}-12-31`,
        });
      }
    }

    if (selectedPeriod?.year && !selectedPeriod?.quarter) {
      setDateRange({
        start_date: `${selectedPeriod.year}-01-01`,
        end_date: `${selectedPeriod.year}-12-31`,
      });
    }

    setSelectedReportList(contentsWithIds);
    setOpenCreateModal(false);
  };

  return (
    <Box
      sx={{
        position: "absolute",
        top: "50%",
        left: "50%",
        transform: "translate(-50%, -50%)",
        width: "80%",
        height: "80%",
        backgroundColor: "white",
        borderRadius: "8px",
        padding: "20px",
      }}
    >
      {/* header */}
      <Box>
        <Typography variant="h4" sx={{ fontWeight: "bold" }}>
          {t("CreateNewReport")}
        </Typography>
        <Typography variant="h6" color="var(--text-secondary-color)">
          {t("MsgSelectNewReportType")}
        </Typography>
        <Divider sx={{ my: 2 }} />
      </Box>
      {/* content */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          width: "100%",
          height: "80%",
          gap: 2,
        }}
      >
        <Box
          sx={{
            // flexWrap: "wrap",
            p: 1,
            width: "50%",
            height: "100%",
          }}
        >
          <Typography
            variant="h6"
            sx={{
              display: "flex",
              alignItems: "center",
              mb: 1,
              fontWeight: "bold",
              gap: "3px",
            }}
          >
            <DescriptionIcon />
            {t("SelectTemplate")}
          </Typography>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              overflowY: "auto",
              height: "90%",
              paddingRight: "8px",
              "&::-webkit-scrollbar": {
                width: "8px",
              },
              "&::-webkit-scrollbar-thumb": {
                backgroundColor: "#ccc",
                borderRadius: "10px",
              },
              // "&::-webkit-scrollbar-track": {
              //   backgroundColor: "transparent",
              // },
            }}
          >
            {templateList.map((item) => (
              <Box
                sx={{
                  width: "100%",
                  borderRadius: 2,
                  height: "80px",
                  display: "flex",
                  p: 2,
                  flexDirection: "column",
                  mb: 2,
                  backgroundColor: selectedTemplate === item ? "var(--custom-color-theme-bg)" : "#fff",
                  border: selectedTemplate === item ? "2px solid var(--button-color)" : "2px solid #ccc",
                  cursor: "pointer",
                  position: "relative",
                }}
                onClick={() => {
                  setSelectedTemplate(item);
                }}
              >
                <Box
                  sx={{
                    width: "100%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    mb: "5px",
                  }}
                >
                  <Typography fontWeight="bold" variant="body1">
                    {item.name}
                  </Typography>
                  {selectedTemplate === item && (
                    <Box
                      sx={{
                        width: 20,
                        height: 20,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        position: "absolute",
                        top: 30,
                        right: 10,
                      }}
                    >
                      <ArrowForwardIosIcon sx={{ color: "var(--button-color)" }} />
                    </Box>
                  )}
                </Box>
                <Typography variant="body1" color="var(--text-secondary-color)">
                  {item.description || "-"}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>
        <Box
          sx={{
            width: "50%",
            height: "90%",
            overflowY: "auto",
          }}
        >
          <Typography
            variant="h6"
            sx={{
              display: "flex",
              alignItems: "center",
              mb: 1,
              fontWeight: "bold",
              gap: "3px",
            }}
          >
            <VisibilityIcon />
            {t("TemplatePreview")}
          </Typography>
          <Box>
            {selectedTemplate ? (
              <Box>
                <Typography variant="h6">{selectedTemplate.name}</Typography>
                {selectedTemplate.description !== "" && (
                  <Typography variant="body1">{selectedTemplate.description}</Typography>
                )}
                <Divider />
                {(selectedTemplate.name.includes("연간") || selectedTemplate.name.includes("분기")) && (
                  <Box sx={{ my: 1 }}>
                    <Typography
                      variant="body1"
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        my: 1,
                        fontWeight: "bold",
                      }}
                    >
                      <CalendarToday sx={{ color: "var(--button-color)", mr: "5px" }} />
                      {t("ReportDateRangeSetting")}
                    </Typography>
                    <Box>
                      <PeriodSelector
                        type={selectedTemplate?.name.includes("분기") ? "quarterly" : "yearly"}
                        onChange={handlePeriodChange}
                      />
                    </Box>
                  </Box>
                )}
                <Divider />
                <Box sx={{ my: 1 }}>
                  <Typography
                    variant="body1"
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      my: 1,
                      fontWeight: "bold",
                    }}
                  >
                    <BackupTableIcon sx={{ color: "var(--button-color)", mr: "5px" }} />
                    {t("LayoutPreview")}
                  </Typography>
                  {selectedTemContents.length > 0 && (
                    <Typography variant="body2">
                      각 섹션 구성의 크기와 배치를 확인하세요. 해당 항목들은 모두 수정이 가능합니다.
                    </Typography>
                  )}
                </Box>
                <Box sx={{ height: "100%", mt: 2 }}>
                  {selectedTemContents.length > 0 ? (
                    <GridPreview contents={selectedTemContents} />
                  ) : (
                    <>
                      <EmptyTemplate />
                    </>
                  )}
                </Box>
              </Box>
            ) : (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "var(--text-secondary-color)",
                  p: 3,
                }}
              >
                <FolderOffIcon
                  sx={{
                    fontSize: 60,
                    mb: 2,
                    color: "action.disabled",
                  }}
                />
                <Typography
                  variant="h6"
                  sx={{
                    mb: 1,
                    color: "var(--text-secondary-color)",
                    textAlign: "center",
                  }}
                >
                  {t("MsgSelectTmplate")}
                </Typography>
              </Box>
            )}
          </Box>
        </Box>
      </Box>
      {/* bottom */}
      <Divider />
      <Box
        sx={{
          position: "sticky",
          zIndex: 999,
          display: "flex",
          justifyContent: "flex-end",
          width: "100%",
          pt: 1,
        }}
      >
        <Button onClick={handleCancel}>{t("Cancel")}</Button>
        <Button sx={{ ...ContainedButtonStyles }} onClick={handleApply}>
          {t("Apply")}
        </Button>
      </Box>
    </Box>
  );
}

// 레이아웃 미리보기
export function GridPreview({ contents }: { contents: ContentItem[] | [] }) {
  return (
    <Box sx={{ backgroundColor: "#F5F6FA", p: 1, py: 2, borderRadius: 2 }}>
      <Grid container spacing={2} sx={{ height: "100%" }}>
        {contents.map((item) => (
          <Grid item xs={item.gridSize} key={item.index}>
            <Box
              sx={{
                height: "90px",
                border: "1px solid var(--tem-preview-border)",
                bgcolor: "transparent",
                background: "linear-gradient(180deg, #F0F7FF 0%, #E8F1FF 100%)",
                borderRadius: 2,
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                alignItems: "center",
                position: "relative",
                padding: "12px",
                transition: "all 0.1s ease-in-out",
                "&:hover": {
                  background: "linear-gradient(180deg, #D9E9FF 0%, #D0E4FF 100%)",
                  boxShadow: "0px 4px 8px rgba(0, 0, 0, 0.1)",
                },
              }}
            >
              <Box
                sx={{
                  position: "absolute",
                  top: "8px",
                  left: "8px",
                  background: "white",
                  borderRadius: "16px",
                  padding: "4px 8px",
                  fontSize: "12px",
                  fontWeight: "bold",
                  color: "var(--tem-preveiw-sec-text)",
                }}
              >
                섹션 {item.index}
              </Box>
              <Typography
                sx={{
                  fontSize: "14px",
                  fontWeight: "bold",
                  mt: "10px",
                  color: "var(--tem-preveiw-type-text)",
                }}
              >
                {REPORT_CONTENTS[item.type]?.title ?? item.type}
              </Typography>
              {!REPORT_CONTENTS[item.type] && (
                <Typography sx={{ fontSize: "11px", color: "#ef4444" }}>미지원 항목 (불러올 때 제외됨)</Typography>
              )}
            </Box>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
}

interface PeriodSelectorProps {
  type: "quarterly" | "yearly";
  onChange: (period: { year: number; quarter?: number }) => void;
}

// 리포트 기간 설정 - 분기/연간 보고서
export function PeriodSelector({ type, onChange }: PeriodSelectorProps) {
  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 5 }, (_, i) => currentYear - i);
  const quarters = [
    { value: 1, label: "1분기 (1월-3월)" },
    { value: 2, label: "2분기 (4월-6월)" },
    { value: 3, label: "3분기 (7월-9월)" },
    { value: 4, label: "4분기 (10월-12월)" },
  ];

  const [selectedYear, setSelectedYear] = useState(currentYear);
  const [selectedQuarter, setSelectedQuarter] = useState(1);

  useEffect(() => {
    onChange({
      year: currentYear,
      quarter: type === "quarterly" ? selectedQuarter : undefined,
    });
  }, []);

  const handleYearChange = (year: number) => {
    setSelectedYear(year);
    onChange({
      year,
      quarter: type === "quarterly" ? selectedQuarter : undefined,
    });
  };

  const handleQuarterChange = (quarter: number) => {
    setSelectedQuarter(quarter);
    onChange({ year: selectedYear, quarter });
  };

  return (
    <Box sx={{ display: "flex", gap: "3px" }}>
      <Select
        value={selectedYear}
        onChange={(e) => handleYearChange(Number(e.target.value))}
        sx={{
          "& .MuiSelect-select": {
            padding: "8px",
          },
        }}
      >
        {years.map((year) => (
          <MenuItem key={year} value={year}>
            {year}년
          </MenuItem>
        ))}
      </Select>
      {type === "quarterly" && (
        <Select
          value={selectedQuarter}
          onChange={(e) => handleQuarterChange(Number(e.target.value))}
          sx={{
            "& .MuiSelect-select": {
              padding: "8px",
            },
          }}
        >
          {quarters.map((quarter) => (
            <MenuItem key={quarter.value} value={quarter.value}>
              {quarter.label}
            </MenuItem>
          ))}
        </Select>
      )}
    </Box>
  );
}
